import { Form, MetaFunction, useLoaderData, ActionFunctionArgs, redirect } from 'react-router'
import { useState } from 'react'
import ListTier from './ListTier'
import { proPlans } from '~/features/premium/proPlans'
import { getLang } from '~/lib/getLang'
import { getStoreFrontProps } from '~/lib/stripe/storeFrontProps'
import useTranslator from '~/lib/useTranslator'
import { getSession } from '~/lib/auth/getSession'
import { purchaseDataCookie } from '~/lib/auth/cookies'
import Navigation from '~/components/Navigation'
import Footer from '~/components/Footer'
import { mergeMeta } from '~/utils/mergeMeta'
import { isWithinInterval, parseISO } from 'date-fns'
import { json } from '~/utils/json'
import { Route } from './+types/route'
import { getLocaleFromRequest } from '~/utils/getLocale'

export const meta: MetaFunction = mergeMeta(() => [
  { title: 'Heja | Upgrade to Heja Pro' },
  {
    name: 'description',
    content:
      'Free app for your sports team. The only app you need to easily handle communication between coaches, managers, parents and players. Management, scheduling, availability, messaging, roster.',
  },
])

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url)
  const props = await getStoreFrontProps(request)
  const lang = getLang({ request })
  const locale = getLocaleFromRequest(request)

  const showBlackFriday =
    url.searchParams.get('showBlackFriday') === '1' ||
    isWithinInterval(new Date(), {
      start: parseISO('2024-11-28'),
      end: parseISO('2024-12-04'),
    })

  const session = await getSession(request, false)

  const proPlan = proPlans(lang)[1]
  const unlimitedPlan = proPlans(lang)[2]

  return json({
    ...props,
    showBlackFriday,
    lang,
    locale,
    proPlan,
    unlimitedPlan,
    isLoggedIn: !!session?.authToken,
  })
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const data = Object.fromEntries(formData)

  throw redirect('/stripe/redirect', {
    headers: { 'Set-Cookie': await purchaseDataCookie.serialize(JSON.stringify(data)) },
  })
}

export default function PremiumUpgrade() {
  const Translator = useTranslator()
  const {
    products,
    coupon,
    currency: initCurrency,
    userId,
    returnUrl,
    proPlan,
    unlimitedPlan,
    showBlackFriday,
    locale,
  } = useLoaderData<typeof loader>()
  const [interval, setInterval] = useState('month')
  const [currency, setCurrency] = useState(initCurrency || 'usd')
  const currencies = Object.keys(products[0]?.prices[0]?.currency_options ?? [])

  return (
    <>
      <Navigation color="purple" />
      {showBlackFriday && (
        <Form method="post" className="w-full max-w-2xl mx-auto flex flex-col gap-4 px-4 md:px-0">
          <input type="hidden" name="returnUrl" value={returnUrl} />
          <input type="hidden" name="interval" value={interval} />
          <input type="hidden" name="currency" value={currency} />
          <input
            type="hidden"
            name="coupon"
            value={process.env.NODE_ENV === 'production' ? 'N0sNoxrW' : 'BVObDXms'}
          />
          <input type="hidden" name="userId" value={userId} />
          <input
            type="hidden"
            name="priceId"
            value={
              process.env.NODE_ENV === 'production'
                ? 'price_1Pnb2kF4WNVM80ZCiZw32tUY'
                : 'price_1Pnb8RF4WNVM80ZCIhnAyu37'
            }
          />

          <div className="rounded-2xl p-1 bg-linear-to-tr mt-8 from-black to-neutral-800">
            <div className="bg-linear-to-tr py-3 px-4 to-neutral-600 from-black rounded-[12px] shadow-md text-white">
              <div className="flex flex-col gap-2">
                <h4 className="text-xl font-semibold text-white/40">
                  {Translator.singular('Black Friday Special')}
                </h4>
                <h3 className="text-white text-balance">
                  {Translator.singular(
                    'Save $39.99 on Team Pro Max and enjoy an ad-free experience.'
                  )}
                </h3>
                <p className="text-white/40 text-sm text-balance font-normal">
                  {Translator.singular('Yearly plan • Renews at regular price')}
                </p>
              </div>
              <button
                type="submit"
                className="text-white px-4 py-2 mt-5 mb-2 font-normal rounded-md w-full md:w-auto bg-premium-color"
              >
                {Translator.singular('Start 14 day trial')}
              </button>
            </div>
          </div>
        </Form>
      )}

      <Form method="post" className="w-full max-w-2xl mx-auto flex flex-col gap-4 px-4 md:px-0">
        <input type="hidden" name="userId" value={userId} />
        <input type="hidden" name="returnUrl" value={returnUrl} />
        <input type="hidden" name="interval" value={interval} />
        <input type="hidden" name="currency" value={currency} />
        {coupon?.id && <input type="hidden" name="coupon" value={coupon.id} />}

        <div className="mx-2 mt-4">
          <div className="flex justify-between items-center my-2">
            <div className="bg-white rounded-md p-0.5 flex shadow-xs text-sm">
              <button
                type="button"
                onClick={() => setInterval('month')}
                className={`rounded-md cursor-pointer py-0.5 px-2 text-black/80 ${
                  interval === 'month' ? 'bg-slate-100 text-black' : 'bg-transparent'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setInterval('year')}
                className={`rounded-md cursor-pointer py-0.5 px-2 text-black/80 ${
                  interval === 'year' ? 'bg-slate-100 text-black' : 'bg-transparent'
                }`}
              >
                Yearly
              </button>
            </div>
            <select
              onChange={(e) => {
                setCurrency(e.target.value)
              }}
              value={currency}
            >
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md">
          {coupon && (
            <div className="bg-red-100 p-4 rounded-t-2xl flex flex-row justify-between gap-1 text-red-700 text-xl">
              <div>{Translator.singular('Discount applied')}</div>
              <div>
                {Translator.singular('{{ percent }}% OFF', {
                  percent: String(coupon.percent_off ?? 0),
                })}
              </div>
            </div>
          )}
          {products.map((p, i) => (
            <ListTier
              key={i}
              product={p}
              currency={currency}
              coupon={coupon}
              onSubmit={() => {}}
              interval={interval}
              locale={locale}
            />
          ))}
        </div>
        <hr />
        <div className="bg-white rounded-2xl shadow-md">
          <div className="px-6 py-5 flex flex-col gap-4">
            <h3 className="text-xl font-semibold text-black-text">
              {Translator.singular('Included in Team Pro')}
            </h3>
            <hr />
            <div className="grid grid-cols-2 gap-4">
              {proPlan?.features.map((f) => (
                <div key={f.title} className="leading-relaxed">
                  <h6 className="text-lg font-normal">{f.title}</h6>
                  <div className="text-black/50">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-md mt-4">
          <div className="px-6 py-5 flex flex-col gap-4">
            <h3 className="text-xl font-semibold text-black-text">
              {Translator.singular('Included in Team Pro Max')}
            </h3>
            <hr />
            <p>All included in Team Pro, plus:</p>
            {unlimitedPlan?.features.map((f) => (
              <div key={f.title}>
                <h6 className="text-lg font-normal">{f.title}</h6>
                <div className="text-black/50">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Form>

      <Footer />
    </>
  )
}
